/*
 * Assign flow client state — V1.9.1 child / guardian / teen records.
 *
 * The V1.9.1 design's richer route tree (children, guardian modes, teen
 * view-only access) is NEW presentation + client state layered over the
 * existing single-email allocation API. Routes that end in an email invite
 * (someone-else-adult, guestparent parent-email) submit through the REAL
 * my-api allocation call; the semantics the current API cannot express live
 * here, in a clearly-seamed client store, and are NEVER presented as server
 * writes.
 *
 * // TODO WIRE: child-record persistence — POST/GET a real my-api child
 * //   contract (two pools via `guest`, per PRODUCT-RULES §Children) instead
 * //   of this localStorage store. Shape mirrors the handoff §State table:
 * //   `children: [{ key, guest }]` + `child_{key}` records.
 * // TODO WIRE: seat assignment records for modes 'child' | 'guestchild' and
 * //   the teen my-route (minor + guardian) — my-api holder allocation cannot
 * //   express a child holder / guardian waiver routing / teen view-only
 * //   login yet. Records are stamped by seat key
 * //   ({orderRef}|{ticketIndex}.{seatIndex}) and merged over the my-api seat
 * //   state for display only.
 * // TODO WIRE: teen view-only login issuance — recording `teenEmail` here
 * //   sends NOTHING; the done copy claims a sent login only when the server
 * //   seam reports it sent one (teenLoginEmailSent flag, mirroring the
 * //   inviteEmailSent honesty pattern).
 */

// ---------------------------------------------------------------------------
// Shapes (handoff README §State)
// ---------------------------------------------------------------------------

/** One child record. `guest: false` = the purchaser's OWN child; `guest: true`
 *  = another parent's child the purchaser signs for under a stated permission.
 *  TWO POOLS, NEVER MERGED — the legal basis for signing differs. */
export interface ChildRecord {
  key: string
  guest: boolean
  first: string
  last: string
  dobD: string
  dobM: string
  dobY: string
  gender: string
}

export type LocalAssignMode = 'child' | 'guestchild' | 'guestparent' | 'teen'

/** Full child PROFILE record — the handoff §State `child_{key}` shape (same
 *  field map the profile flow edits for the account holder). Identity facts
 *  (name / DOB / gender) are mirrored onto the ChildRecord row on save so the
 *  Profile list and the assign picker read one set of facts.
 *  // TODO WIRE: my-api has no child-profile contract yet — these records are
 *  //   the purchaser's own typed input, persisted client-side ONLY and never
 *  //   presented as server writes (same seam class as ChildRecord above). */
export type ChildProfileDraft = import('../wizard/wizardModel').WizardDraft

/** A locally-recorded seat assignment for the modes the allocation API cannot
 *  express yet (see the TODO WIRE seam above). */
export interface LocalAssignment {
  seatKey: string
  mode: LocalAssignMode
  /** Holder display name (child/teen name; '' when unknown on guestparent). */
  name: string
  childKey: string
  tType: string
  evName: string
  parentEmail: string
  parentName: string
  minor: boolean
  teenLogin: boolean
  teenEmail: string
}

interface StoreShape {
  children: ChildRecord[]
  assigned: Record<string, LocalAssignment>
  /** `child_{key}` profile records keyed by ChildRecord.key (handoff §State). */
  childProfiles: Record<string, ChildProfileDraft>
}

// ---------------------------------------------------------------------------
// Store — module-level + localStorage persistence, useSyncExternalStore-ready
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'tru.assignStore.v1'

function readStored(): StoreShape {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { children: [], assigned: {}, childProfiles: {} }
    const parsed = JSON.parse(raw) as Partial<StoreShape>
    return {
      children: Array.isArray(parsed.children) ? parsed.children : [],
      assigned: parsed.assigned && typeof parsed.assigned === 'object' ? parsed.assigned : {},
      childProfiles:
        parsed.childProfiles && typeof parsed.childProfiles === 'object' ? parsed.childProfiles : {},
    }
  } catch {
    return { children: [], assigned: {}, childProfiles: {} }
  }
}

let state: StoreShape = readStored()
const listeners = new Set<() => void>()

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Persistence unavailable (private mode etc.) — in-session state still applies.
  }
  listeners.forEach((l) => l())
}

export function subscribeAssignStore(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

export const getAssignStoreSnapshot = (): StoreShape => state

/** All children in ONE pool — callers must filter by `guest` (the picker only
 *  ever offers the pool matching the chosen route). */
export function listChildren(): ChildRecord[] {
  return state.children
}

export function addChild(child: Omit<ChildRecord, 'key'>): ChildRecord {
  const rec: ChildRecord = { ...child, key: `k${Date.now()}${Math.floor(Math.random() * 1e4)}` }
  state = { ...state, children: [...state.children, rec] }
  persist()
  return rec
}

export function getLocalAssignment(seatKey: string): LocalAssignment | null {
  return state.assigned[seatKey] ?? null
}

export function recordLocalAssignment(a: LocalAssignment): void {
  state = { ...state, assigned: { ...state.assigned, [a.seatKey]: a } }
  persist()
}

export function clearLocalAssignment(seatKey: string): void {
  if (!(seatKey in state.assigned)) return
  const next = { ...state.assigned }
  delete next[seatKey]
  state = { ...state, assigned: next }
  persist()
}

/** Child profile record (`child_{key}`) — null until first saved. */
export function getChildProfile(key: string): ChildProfileDraft | null {
  return state.childProfiles[key] ?? null
}

/** Save a child's profile draft AND mirror the identity facts (name / DOB /
 *  gender) onto the ChildRecord row, so the Profile list, the assign picker
 *  and the flow all read ONE set of facts. `guest` is NEVER touched here —
 *  a record can never migrate between the two pools by editing a profile. */
export function saveChildProfile(key: string, draft: ChildProfileDraft): void {
  state = {
    ...state,
    childProfiles: { ...state.childProfiles, [key]: draft },
    children: state.children.map((c) =>
      c.key === key
        ? {
            ...c,
            first: draft.first, last: draft.last,
            dobD: draft.dobD, dobM: draft.dobM, dobY: draft.dobY,
            gender: draft.gender,
          }
        : c,
    ),
  }
  persist()
}

/** TEST-ONLY reset. */
export function resetAssignStoreForTests(): void {
  state = { children: [], assigned: {}, childProfiles: {} }
  try { localStorage.removeItem(STORAGE_KEY) } catch { /* noop */ }
  listeners.forEach((l) => l())
}
